import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Search } from '@mui/icons-material';
import CustomPagination from '../../components/common/CustomPagination';
import { fetchAffiliateRewards } from '../../services/affiliate';

const addSerialNumbers = (data, page, pageSize) =>
  data.map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));

const AffiliateListPage = () => {
  const { t } = useTranslation(['affiliate', 'common']);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    partner: '',
  });

  // Debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedSearch = useDebounce(filters.search, 500);
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Fetch affiliate rewards/orders
  const fetchRewards = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: pageSize,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.partner) {
        params.partner = filters.partner;
      }

      const response = await fetchAffiliateRewards(params);

      if (response.results) {
        setRewards(response.results);
        setTotalCount(response.count || response.results.length);
      } else {
        setRewards(Array.isArray(response) ? response : []);
        setTotalCount(Array.isArray(response) ? response.length : 0);
      }
    } catch (error) {
      console.error('Failed to fetch affiliate rewards:', error);
      setRewards([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, filters.status, filters.partner]);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  const handlePageChange = (newPage) => {
    setPage(Math.max(0, Math.min(newPage, totalPages - 1)));
  };

  const handleRowsPerPageChange = (event) => {
    const value = event?.target?.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      setPageSize(numericValue);
      setPage(0);
    }
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0 && value <= 1000) {
      setPageSize(value);
      setPage(0);
    }
    setCustomRowsInput('');
    setCustomRowsActive(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'paid':
        return 'info';
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    return t(`affiliate:status.${status}`, status);
  };

  const getOrderStatusLabel = (status) => {
    const statusMap = {
      1: t('affiliate:orderStatus.unpaid'),
      2: t('affiliate:orderStatus.processing'),
      3: t('affiliate:orderStatus.purchased'),
      4: t('affiliate:orderStatus.inWarehouse'),
      5: t('affiliate:orderStatus.shippedOut'),
      6: t('affiliate:orderStatus.inNigeria'),
      7: t('affiliate:orderStatus.received'),
      8: t('affiliate:orderStatus.refunded'),
      9: t('affiliate:orderStatus.success'),
      10: t('affiliate:orderStatus.closed'),
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      field: 'sn',
      headerName: t('affiliate:list.table.sn'),
      width: 80,
      sortable: false,
    },
    {
      field: 'order_number',
      headerName: t('affiliate:list.table.orderNumber'),
      width: 180,
      renderCell: (params) => {
        // order_number is provided directly by serializer
        return params.row.order_number || '-';
      },
    },
    {
      field: 'customer',
      headerName: t('affiliate:list.table.customer'),
      width: 200,
      renderCell: (params) => {
        // customer_username and customer_email are provided directly by serializer
        const username = params.row.customer_username;
        const email = params.row.customer_email;
        if (username) return username;
        if (email) return email;
        return '-';
      },
    },
    {
      field: 'affiliate',
      headerName: t('affiliate:list.table.affiliate'),
      width: 200,
      renderCell: (params) => {
        // partner_username is provided directly by serializer
        return params.row.partner_username || '-';
      },
    },
    {
      field: 'order_status',
      headerName: t('affiliate:list.table.orderStatus'),
      width: 150,
      renderCell: (params) => {
        // order_status is provided directly by serializer
        const status = params.row.order_status;
        if (status === undefined || status === null) return '-';
        const statusColor = status === 9 ? 'success' : status === 8 ? 'error' : 'default';
        return (
          <Chip
            label={getOrderStatusLabel(status)}
            color={statusColor}
            size="small"
          />
        );
      },
    },
    {
      field: 'order_amount',
      headerName: t('affiliate:list.table.orderAmount'),
      width: 200,
      renderCell: (params) => {
        // Show both RMB and Naira
        const orderTotalRmb = params.row.order_total_rmb;
        const orderTotalNaira = params.row.order_total;
        
        if (orderTotalRmb !== undefined && orderTotalRmb !== null && orderTotalNaira !== undefined && orderTotalNaira !== null) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ¥{Number(orderTotalRmb).toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                ₦{Number(orderTotalNaira).toFixed(2)}
              </Typography>
            </Box>
          );
        }
        
        if (orderTotalNaira !== undefined && orderTotalNaira !== null) {
          return `₦${Number(orderTotalNaira).toFixed(2)}`;
        }
        
        return '-';
      },
    },
    {
      field: 'reward_amount',
      headerName: t('affiliate:list.table.rewardAmount'),
      width: 200,
      renderCell: (params) => {
        // Show both RMB and Naira
        const rewardAmountRmb = params.row.reward_amount_rmb;
        const rewardAmountNaira = params.row.reward_amount;
        
        if (rewardAmountRmb !== undefined && rewardAmountRmb !== null && rewardAmountNaira !== undefined && rewardAmountNaira !== null) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ¥{Number(rewardAmountRmb).toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                ₦{Number(rewardAmountNaira).toFixed(2)}
              </Typography>
            </Box>
          );
        }
        
        if (rewardAmountNaira !== undefined && rewardAmountNaira !== null) {
          return `₦${Number(rewardAmountNaira).toFixed(2)}`;
        }
        
        return '-';
      },
    },
    {
      field: 'reward_status',
      headerName: t('affiliate:list.table.rewardStatus'),
      width: 150,
      renderCell: (params) => {
        const status = params.row.status;
        if (!status) return '-';
        return (
          <Chip
            label={getStatusLabel(status)}
            color={getStatusColor(status)}
            size="small"
          />
        );
      },
    },
    {
      field: 'calculated_at',
      headerName: t('affiliate:list.table.calculatedAt'),
      width: 180,
      renderCell: (params) => {
        const date = params.row.calculated_at;
        if (!date) return '-';
        try {
          const dateObj = new Date(date);
          return dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        } catch (e) {
          return date;
        }
      },
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#DC143C" sx={{ mb: 2 }}>
          {t('affiliate:list.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('affiliate:list.description')}
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('affiliate:list.filters.search')}
                variant="outlined"
                size="small"
                fullWidth
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('affiliate:list.filters.status')}</InputLabel>
                <Select
                  value={filters.status}
                  label={t('affiliate:list.filters.status')}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="all">{t('affiliate:list.filters.allStatuses')}</MenuItem>
                  <MenuItem value="pending">{t('affiliate:status.pending')}</MenuItem>
                  <MenuItem value="approved">{t('affiliate:status.approved')}</MenuItem>
                  <MenuItem value="paid">{t('affiliate:status.paid')}</MenuItem>
                  <MenuItem value="refunded">{t('affiliate:status.refunded')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchRewards}
                  disabled={loading}
                >
                  {t('common:refresh')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={addSerialNumbers(rewards, page, pageSize)}
              columns={columns}
              loading={loading}
              hideFooter
              paginationMode="server"
              rowCount={totalCount}
              page={page}
              pageSize={pageSize}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'white'
                },
              }}
            />
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              rowsPerPage={pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              customRowsActive={customRowsActive}
              customRowsInput={customRowsInput}
              onCustomRowsInputChange={(event) => setCustomRowsInput(event.target.value)}
              onCustomRowsInputBlur={handleCustomRowsInputBlur}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AffiliateListPage;

